import type { UseFormReturn } from 'react-hook-form';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Labeled, Menu, MenuItem } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { ISortableStackRenderProps } from '~/components/atoms/fields/SortableStack';
import type { IContainerProps } from '~/components/utils/Container';
import type { ITabsSettingsFormValues } from './TabsSettingsForm';
import { SortableStack } from '~/components/atoms/fields/SortableStack';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { getTabProps, tabsSettingsTabIds } from './helpers';
import { TabsSettingsTab } from './TabsSettingsTab';

export type ITabsSettingsFormListSectionProps = IContainerProps & {
  form: UseFormReturn<ITabsSettingsFormValues>;
};

const styles = stylex.create({
  tabsContainer: {
    backgroundColor: colorSchemeTokens.surface,
    position: 'relative',
    overflow: 'hidden',
    borderLeftWidth: 12,
    borderBottomWidth: 12,
    borderRightWidth: 12,
    borderBottomLeftRadius: 36,
    borderBottomRightRadius: 40,
    borderColor: colorSchemeTokens.outlineVariant,
    width: 640,
    overflowX: 'auto',
  },
  tabList: {
    flexGrow: 1,
    borderTopWidth: 1,
    borderColor: colorSchemeTokens.outlineVariant,
    marginTop: 16,
  },
  tab: {
    paddingTop: 12,
    paddingBottom: 12,
  },
  addButton: {
    flexShrink: 0,
    flexGrow: 1,
    flexBasis: 0,
    display: 'flex',
    justifyContent: 'center',
  },
  menu: {
    minWidth: 260,
    height: 220,
  },
});

export const TabsSettingsFormListSection: React.FC<
  ITabsSettingsFormListSectionProps
> = (props) => {
  const { sx, form } = props;

  const { t } = useTranslation();
  const tabIds = form.watch('tabIds');
  const addableTabIds = tabsSettingsTabIds.filter(
    (id) => !tabIds?.includes(id),
  );

  return (
    <FormSection sx={sx}>
      <Labeled
        label={t('TabsSettingsFormListSection.fields.reorganizeTabs.label')}
        supportingText={t(
          'TabsSettingsFormListSection.fields.reorganizeTabs.supportingText',
        )}
      >
        <div {...stylex.props(styles.tabsContainer)}>
          <FormField
            as={SortableStack}
            horizontal
            gap={2}
            sx={styles.tabList}
            name="tabIds"
            control={form.control}
            end={
              <div {...stylex.props(styles.addButton)}>
                <Menu
                  trigger={({ getProps }) => (
                    <IconButton
                      variant="filled"
                      icon={<FontAwesomeIcon icon={faPlus} />}
                      disabled={addableTabIds.length <= 0}
                      {...getProps()}
                    />
                  )}
                  placement="bottom-start"
                  sx={styles.menu}
                >
                  {addableTabIds
                    .map((tabId) => getTabProps(tabId, t))
                    .map(({ id, icon, label, supportingText }) => {
                      let disabled = false;
                      disabled ||= id === 'chat' && !form.watch('chatEnabled');
                      disabled ||=
                        id === 'merchmmf' && !form.watch('merchMMFEnabled');
                      disabled ||=
                        id === 'merchwp' && !form.watch('merchWPEnabled');
                      disabled ||=
                        id === 'leaderboard' &&
                        !form.watch('leaderboardEnabled');
                      disabled ||=
                        id === 'voting' && !form.watch('leaderboardEnabled');
                      disabled ||=
                        id === 'submissions' &&
                        !form.watch('leaderboardEnabled');

                      return (
                        <MenuItem
                          key={id}
                          label={label}
                          leadingIcon={icon}
                          onClick={() =>
                            form.setValue('tabIds', [...(tabIds ?? []), id])
                          }
                          disabled={disabled}
                          supportingText={supportingText}
                        />
                      );
                    })}
                </Menu>
              </div>
            }
            itemRenderer={({ id }: ISortableStackRenderProps) => (
              <TabsSettingsTab
                sx={styles.tab}
                active={id === form.watch('startTabId')}
                key={id}
                onDelete={
                  !['today'].includes(id)
                    ? () => {
                        form.setValue(
                          'tabIds',
                          tabIds?.filter((tabId) => tabId !== id) ?? [],
                        );
                        if (form.watch('startTabId') === id) {
                          form.setValue('startTabId', tabIds?.[0]);
                        }
                      }
                    : undefined
                }
                {...getTabProps(id, t)}
              />
            )}
          />
        </div>
      </Labeled>
    </FormSection>
  );
};
